/**
 * LevelSelect — Level selection screen for The Cats of Chronos.
 *
 * Features:
 * - 4 level cards arranged horizontally
 * - Locked levels shown dimmed with a lock indicator
 * - Completion status shown per level
 * - Atmospheric background with subtle particles
 * - Back button to return to title screen
 * - Selecting an unlocked level triggers scene transition
 * - Keyboard arrow / gamepad D-pad focus navigation (skips locked cards)
 * - Enter / gamepad A selects focused unlocked card
 * - 1-4 number shortcuts still work
 * - scaledFont applied to all text via AccessibilitySystem
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Grid } from "@babylonjs/gui/2D/controls/grid";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { Button } from "@babylonjs/gui/2D/controls/button";
import { Control } from "@babylonjs/gui/2D/controls/control";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { COLORS } from "../config/GameConfig";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { SaveSystem } from "../systems/SaveSystem";
import { ALL_LEVELS } from "../levels/LevelRegistry";
import type { LevelDefinition } from "../levels/LevelDefinition";
import { configureResponsiveFullscreenUI } from "../ui/guiScaling";
import { scaledFont } from "../ui/menuFonts";
import { isAnyGamepadButtonPressed } from "../ui/ControlGlyphs";
import { resolveFocusTarget } from "../ui/menuFocus";

export interface LevelSelectContext {
  scene: Scene;
  dispose: () => void;
}

/** Injectable override for isLevelUnlocked — used in tests to control lock state. */
export type IsLevelUnlockedFn = (level: LevelDefinition, saveSystem: SaveSystem) => boolean;

/** Visual style applied to the focused card (mirrors hover style). */
const FOCUS_COLOR = COLORS.BIOLUMINESCENT_GREEN;
const FOCUS_BG = "rgba(60, 45, 85, 0.95)";
const FOCUS_THICKNESS = 3;
const DEFAULT_CARD_COLOR = COLORS.BRASS;
const DEFAULT_CARD_BG = "rgba(42, 31, 61, 0.85)";

/**
 * Set up the Level Select scene.
 * @param scene  - The Babylon scene to render into.
 * @param isLevelUnlockedFn - Optional override for unlock check (injectable for tests).
 */
export function setupLevelSelect(
  scene: Scene,
  isLevelUnlockedFn: IsLevelUnlockedFn = isLevelUnlocked,
): LevelSelectContext {
  const saveSystem = SaveSystem.getInstance();

  // --- Clear scene ---
  scene.clearColor = new Color4(0.03, 0.015, 0.05, 1.0);

  // --- Camera ---
  const camera = new FreeCamera("levelSelectCamera", new Vector3(0, 0, -15), scene);
  camera.setTarget(Vector3.Zero());

  // --- Lighting ---
  const ambientLight = new HemisphericLight("lsAmbient", new Vector3(0, 1, 0), scene);
  ambientLight.intensity = 0.4;
  ambientLight.diffuse = Color3.FromHexString(COLORS.DEEP_PURPLE);

  // --- Subtle background particles ---
  const particles = new ParticleSystem("lsParticles", 60, scene);
  particles.createPointEmitter(new Vector3(-10, -8, 0), new Vector3(10, 8, 0));
  particles.minLifeTime = 4;
  particles.maxLifeTime = 10;
  particles.minSize = 0.02;
  particles.maxSize = 0.08;
  particles.emitRate = 8;
  particles.gravity = new Vector3(0, 0.15, 0);
  particles.color1 = Color4.FromHexString(COLORS.BIOLUMINESCENT_GREEN + "66");
  particles.color2 = Color4.FromHexString(COLORS.BIOLUMINESCENT_PURPLE + "44");
  particles.colorDead = new Color4(0, 0, 0, 0);
  particles.minEmitPower = 0.1;
  particles.maxEmitPower = 0.4;
  particles.minEmitBox = new Vector3(-12, -8, 4);
  particles.maxEmitBox = new Vector3(12, 8, 8);
  particles.start();

  // --- GUI ---
  const gui = AdvancedDynamicTexture.CreateFullscreenUI("levelSelectUI", true, scene);
  const disposeGuiScaling = configureResponsiveFullscreenUI(gui, scene);
  const renderWidth = scene.getEngine().getRenderingCanvas()?.clientWidth ?? window.innerWidth;
  const columns = renderWidth < 1200 ? 2 : ALL_LEVELS.length;
  const rows = Math.ceil(ALL_LEVELS.length / columns);
  const compactLayout = columns < ALL_LEVELS.length;

  // Header
  const header = new TextBlock("lsHeader");
  header.text = "Select a Level";
  header.color = COLORS.BIOLUMINESCENT_GREEN;
  header.fontSize = scaledFont(52);
  header.fontFamily = "Georgia, 'Times New Roman', serif";
  header.fontWeight = "bold";
  header.height = "82px";
  header.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
  header.top = compactLayout ? "26px" : "32px";
  header.shadowColor = COLORS.DEEP_PURPLE;
  header.shadowBlur = 10;
  gui.addControl(header);

  const instructions = new TextBlock("lsInstructions");
  instructions.text = compactLayout
    ? "Press 1-4, arrow keys, or click a card. Esc returns to the title screen."
    : "Keyboard: press 1-4 or arrow keys to choose a level, Enter to select.";
  instructions.color = "#d4c5a9";
  instructions.fontSize = scaledFont(22);
  instructions.fontFamily = "Georgia, 'Times New Roman', serif";
  instructions.height = "42px";
  instructions.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
  instructions.top = compactLayout ? "82px" : "90px";
  instructions.alpha = 0.95;
  instructions.shadowColor = "rgba(0,0,0,0.75)";
  instructions.shadowBlur = 6;
  gui.addControl(instructions);

  // Level cards grid — switch to a 2x2 layout on laptop-sized widths so the
  // cards stay readable without requiring the entire GUI to shrink.
  const grid = new Grid("levelGrid");
  grid.width = compactLayout ? "88%" : "92%";
  grid.height = compactLayout ? "62%" : "56%";
  grid.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
  grid.top = compactLayout ? "44px" : "30px";

  const columnWidth = 1 / Math.max(columns, 1);
  for (let i = 0; i < columns; i++) {
    grid.addColumnDefinition(columnWidth);
  }
  const rowHeight = 1 / Math.max(rows, 1);
  for (let i = 0; i < rows; i++) {
    grid.addRowDefinition(rowHeight);
  }

  gui.addControl(grid);

  // --- Focus navigation state ---
  // focusable[] = level cards (indices 0..N-1) + back button (index N)
  // enabled[] mask: true = unlocked card or back button (can receive focus)
  const levelCards: Rectangle[] = [];
  const levelUnlocked: boolean[] = [];

  // Create level cards
  for (let i = 0; i < ALL_LEVELS.length; i++) {
    const level = ALL_LEVELS[i];
    const isUnlocked = isLevelUnlockedFn(level, saveSystem);
    const isComplete = saveSystem.isLevelComplete(level.key);
    levelUnlocked.push(isUnlocked);

    const card = createLevelCard(level, i, compactLayout, isUnlocked, isComplete, () => {
      if (isUnlocked) {
        gameEvents.emit(GameEvents.MENU_LEVEL_SELECTED, level.key);
      }
    });

    levelCards.push(card);
    grid.addControl(card, Math.floor(i / columns), i % columns);
  }

  // Back button
  const backBtn = Button.CreateSimpleButton("backBtn", "< Back to Title");
  backBtn.width = "200px";
  backBtn.height = "45px";
  backBtn.color = COLORS.BRASS;
  backBtn.fontSize = scaledFont(20);
  backBtn.fontFamily = "Georgia, 'Times New Roman', serif";
  backBtn.cornerRadius = 6;
  backBtn.thickness = 1.5;
  backBtn.background = "rgba(42, 31, 61, 0.8)";
  backBtn.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
  backBtn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
  backBtn.left = "30px";
  backBtn.top = "-30px";
  backBtn.onPointerClickObservable.add(() => {
    gameEvents.emit(GameEvents.MENU_BACK_PRESSED);
  });
  backBtn.onPointerEnterObservable.add(() => {
    backBtn.background = "rgba(60, 45, 85, 0.9)";
  });
  backBtn.onPointerOutObservable.add(() => {
    backBtn.background = "rgba(42, 31, 61, 0.8)";
  });
  gui.addControl(backBtn);

  // focusable list: level cards (0..N-1), then back button (N)
  // enabled mask: locked cards are NOT focusable via arrow nav
  const focusable: (Rectangle | Button)[] = [...levelCards, backBtn];
  const enabled: boolean[] = [...levelUnlocked, true]; // back button always enabled

  let focusIndex = 0;
  // Start on first enabled entry
  if (!enabled[focusIndex]) {
    focusIndex = resolveFocusTarget(-1, 1, enabled);
  }

  /** Apply focus highlight to the element at focusIndex, clear all others. */
  const applyFocus = (index: number): void => {
    for (let i = 0; i < levelCards.length; i++) {
      const card = levelCards[i];
      if (!levelUnlocked[i]) continue; // locked cards stay dim
      if (i === index) {
        card.color = FOCUS_COLOR;
        card.background = FOCUS_BG;
        card.thickness = FOCUS_THICKNESS;
      } else {
        card.color = DEFAULT_CARD_COLOR;
        card.background = DEFAULT_CARD_BG;
        card.thickness = 2;
      }
    }
    // Back button focus style
    const backIndex = levelCards.length;
    if (index === backIndex) {
      backBtn.background = "rgba(60, 45, 85, 0.9)";
    } else {
      backBtn.background = "rgba(42, 31, 61, 0.8)";
    }
  };

  applyFocus(focusIndex);

  /** Move focus by dir (+1 or -1), skipping locked/disabled entries. */
  const moveFocus = (dir: 1 | -1): void => {
    const next = resolveFocusTarget(focusIndex, dir, enabled);
    if (next !== focusIndex) {
      focusIndex = next;
      applyFocus(focusIndex);
    }
  };

  const selectLevelByIndex = (index: number): void => {
    const level = ALL_LEVELS[index];
    if (!level) return;
    if (isLevelUnlockedFn(level, saveSystem)) {
      gameEvents.emit(GameEvents.MENU_LEVEL_SELECTED, level.key);
    }
  };

  /** Activate whatever is currently focused. */
  const activateFocused = (): void => {
    const backIndex = levelCards.length;
    if (focusIndex === backIndex) {
      gameEvents.emit(GameEvents.MENU_BACK_PRESSED);
    } else if (focusIndex < levelCards.length && enabled[focusIndex]) {
      selectLevelByIndex(focusIndex);
    }
  };

  // Keyboard: arrows navigate focus, Enter activates, 1-4 direct select, Escape goes back
  const handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === "Escape") {
      gameEvents.emit(GameEvents.MENU_BACK_PRESSED);
      evt.preventDefault();
      return;
    }

    if (evt.key === "ArrowRight" || evt.key === "ArrowDown") {
      moveFocus(1);
      evt.preventDefault();
      return;
    }
    if (evt.key === "ArrowLeft" || evt.key === "ArrowUp") {
      moveFocus(-1);
      evt.preventDefault();
      return;
    }

    if (evt.key === "Enter") {
      activateFocused();
      evt.preventDefault();
      return;
    }

    if (/^[1-4]$/.test(evt.key)) {
      selectLevelByIndex(Number(evt.key) - 1);
    }
  };
  window.addEventListener("keydown", handleKeyDown);

  // --- Gamepad poll (onBeforeRenderObservable, edge-detected) ---
  let gpLeftDown = false;
  let gpRightDown = false;
  let gpADown = false;
  let gpBDown = false;
  let gpStartDown = false;

  let gamepadObserver: Observer<Scene> | null = null;
  gamepadObserver = scene.onBeforeRenderObservable.add(() => {
    // D-pad right / right stick right (buttons 15 / axis not mapped here)
    const dpadRight = isAnyGamepadButtonPressed([15]);
    if (dpadRight && !gpRightDown) moveFocus(1);
    gpRightDown = dpadRight;

    // D-pad left (button 14)
    const dpadLeft = isAnyGamepadButtonPressed([14]);
    if (dpadLeft && !gpLeftDown) moveFocus(-1);
    gpLeftDown = dpadLeft;

    // Button 0 (A) — select focused
    const btnA = isAnyGamepadButtonPressed([0]);
    if (btnA && !gpADown) activateFocused();
    gpADown = btnA;

    // Button 1 (B) or button 9 (Start) — go back
    const btnB = isAnyGamepadButtonPressed([1]);
    if (btnB && !gpBDown) gameEvents.emit(GameEvents.MENU_BACK_PRESSED);
    gpBDown = btnB;

    const btnStart = isAnyGamepadButtonPressed([9]);
    if (btnStart && !gpStartDown) gameEvents.emit(GameEvents.MENU_BACK_PRESSED);
    gpStartDown = btnStart;
  });

  // --- Dispose ---
  const dispose = () => {
    window.removeEventListener("keydown", handleKeyDown);
    scene.onBeforeRenderObservable.remove(gamepadObserver);
    disposeGuiScaling();
    particles.dispose();
    gui.dispose();
    ambientLight.dispose();
    camera.dispose();
  };

  return { scene, dispose };
}

/**
 * Check if a level is unlocked based on prerequisites.
 *
 * A level with no prerequisite (the tutorial Atrium) is always open;
 * everything else unlocks when its prerequisite level has been completed
 * at least once. This used to be hard-coded `return true` ("DEBUG:
 * temporarily unlock all levels"), which made Elder rewards and level
 * progression meaningless — and let players open Level 3 cold, missing
 * the co-op abilities its puzzles teach against.
 *
 * Dev builds keep the open-world shortcut via `VITE_UNLOCK_ALL_LEVELS=1`
 * so QA tooling and playtests can jump straight to any level.
 */
export function isLevelUnlocked(level: LevelDefinition, saveSystem: SaveSystem): boolean {
  if (import.meta.env.DEV && import.meta.env.VITE_UNLOCK_ALL_LEVELS === "1") {
    return true;
  }
  if (!level.prerequisite) return true;
  return saveSystem.isLevelComplete(level.prerequisite);
}

/** Create a single level card GUI element. */
function createLevelCard(
  level: LevelDefinition,
  index: number,
  compactLayout: boolean,
  isUnlocked: boolean,
  isComplete: boolean,
  onSelect: () => void,
): Rectangle {
  const card = new Rectangle(`card_${level.key}`);
  card.width = compactLayout ? "92%" : "90%";
  card.height = compactLayout ? "86%" : "88%";
  card.cornerRadius = 16;
  card.thickness = 2.5;
  card.color = isUnlocked ? COLORS.BRASS : "#333333";
  card.background = isUnlocked
    ? "rgba(32, 22, 48, 0.9)"
    : "rgba(20, 15, 30, 0.7)";
  card.paddingLeft = "12px";
  card.paddingRight = "12px";

  // Card content stack
  const stack = new StackPanel(`cardStack_${level.key}`);
  stack.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
  card.addControl(stack);

  const hotkey = new TextBlock(`hotkey_${level.key}`);
  hotkey.text = `[${index + 1}]`;
  hotkey.color = isUnlocked ? COLORS.BIOLUMINESCENT_GREEN : "#666666";
  hotkey.fontSize = scaledFont(18);
  hotkey.fontFamily = "Georgia, 'Times New Roman', serif";
  hotkey.fontWeight = "bold";
  hotkey.height = "28px";
  hotkey.paddingTop = "10px";
  stack.addControl(hotkey);

  // Level number
  const levelNum = new TextBlock(`levelNum_${level.key}`);
  levelNum.text = `Level ${level.number}`;
  levelNum.color = isUnlocked ? COLORS.COPPER : "#555555";
  levelNum.fontSize = scaledFont(18);
  levelNum.fontFamily = "Georgia, 'Times New Roman', serif";
  levelNum.height = "28px";
  stack.addControl(levelNum);

  // Lock icon or checkmark
  const statusIcon = new TextBlock(`status_${level.key}`);
  if (!isUnlocked) {
    statusIcon.text = "\u{1F512}"; // lock emoji
    statusIcon.fontSize = scaledFont(38);
    statusIcon.height = "58px";
    statusIcon.color = "#555555";
  } else if (isComplete) {
    statusIcon.text = "✓"; // checkmark
    statusIcon.fontSize = scaledFont(40);
    statusIcon.height = "58px";
    statusIcon.color = COLORS.BIOLUMINESCENT_GREEN;
  } else {
    statusIcon.text = "◆"; // diamond
    statusIcon.fontSize = scaledFont(32);
    statusIcon.height = "58px";
    statusIcon.color = COLORS.BRASS;
  }
  stack.addControl(statusIcon);

  // Level name
  const nameText = new TextBlock(`name_${level.key}`);
  nameText.text = level.displayName;
  nameText.color = isUnlocked ? "#ffffff" : "#666666";
  nameText.fontSize = scaledFont(22);
  nameText.fontFamily = "Georgia, 'Times New Roman', serif";
  nameText.fontWeight = "bold";
  // Two-line titles ("The Redwood Scrapyard", "The Overgrown
  // Conservatory") need room for a second wrapped line at fontSize 22
  // (~28px/line). The old 38px reserved only one line, so the second
  // line overflowed onto the italic subtitle below it.
  nameText.height = compactLayout ? "70px" : "64px";
  nameText.textWrapping = true;
  stack.addControl(nameText);

  // Subtitle
  const subtitleText = new TextBlock(`subtitle_${level.key}`);
  subtitleText.text = level.subtitle;
  subtitleText.color = isUnlocked ? "#f0d8a2" : "#666666";
  subtitleText.fontSize = scaledFont(16);
  subtitleText.fontFamily = "Georgia, 'Times New Roman', serif";
  subtitleText.fontStyle = "italic";
  subtitleText.height = compactLayout ? "52px" : "44px";
  subtitleText.textWrapping = true;
  stack.addControl(subtitleText);

  // Completion label
  if (isComplete) {
    const completeLabel = new TextBlock(`complete_${level.key}`);
    completeLabel.text = "COMPLETE";
    completeLabel.color = COLORS.BIOLUMINESCENT_GREEN;
    completeLabel.fontSize = scaledFont(15);
    completeLabel.fontFamily = "Georgia, 'Times New Roman', serif";
    completeLabel.fontWeight = "bold";
    completeLabel.height = "28px";
    completeLabel.alpha = 0.95;
    stack.addControl(completeLabel);
  } else if (!isUnlocked) {
    const lockedLabel = new TextBlock(`locked_${level.key}`);
    lockedLabel.text = "LOCKED";
    lockedLabel.color = "#555555";
    lockedLabel.fontSize = scaledFont(15);
    lockedLabel.fontFamily = "Georgia, 'Times New Roman', serif";
    lockedLabel.fontWeight = "bold";
    lockedLabel.height = "28px";
    stack.addControl(lockedLabel);
  }

  // Interaction
  if (isUnlocked) {
    card.isPointerBlocker = true;
    card.onPointerClickObservable.add(onSelect);
    card.onPointerEnterObservable.add(() => {
      card.thickness = 3;
      card.color = COLORS.BIOLUMINESCENT_GREEN;
      card.background = "rgba(60, 45, 85, 0.95)";
    });
    card.onPointerOutObservable.add(() => {
      card.thickness = 2;
      card.color = COLORS.BRASS;
      card.background = "rgba(42, 31, 61, 0.85)";
    });
  }

  return card;
}
